import { type HttpErrorResponse } from '@angular/common/http'
import { Component, Input, type OnDestroy } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { type AddProductCptResult, ApiService, AppointmentDataService, AppointmentModel, ClinicService, ConfirmService, Permission, PermissionsService, ProductModel, STATUS as StudyStatus, StudyModel } from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { combineLatestWith, filter, finalize, map, Observable, of, shareReplay, Subject, switchMap, takeUntil, tap } from 'rxjs'

import { CptDialogComponent, type CptDialogData } from '../cpt-dialog/cpt-dialog.component'
import { ProductDialogComponent } from '../product-dialog'

@Component({
  selector: 'mmx-procedures-card',
  templateUrl: './procedures-card.component.html',
  styleUrls: ['./procedures-card.component.scss'],
  standalone: false,
})
export class ProceduresCardComponent implements OnDestroy {
  @Input() public editable = true
  private readonly enableEditCptDialogSettingKey = 'enableEditCptDialog'
  private readonly baseDisplayedColumns = ['name', 'eid', 'procedureCodes']

  private onDestroy$ = new Subject<void>()
  private currentAppointment: AppointmentModel | null = null
  private currentProductIds = new Set<string>()
  private tableConfigInitialized = false
  /** Ids of products rendered as fabricated rows because no study claims them. */
  private syntheticProductIds = new Set<string>()

  public displayedColumns: string[] = this.baseDisplayedColumns
  public canEditCPT = false
  /** Delete carries the pencil's gate plus the Edit / Reschedule permission. */
  public canDeleteCPT = false
  /** Any rendered row, including the synthesised ones for products on no study. Styling only. */
  public hasStudies = false

  public canAddCPT = false

  /** The row whose delete request is in flight; further clicks are ignored until it settles. */
  public deletingProductId: string | null = null

  loading$ = this.data.progress$

  studies$: Observable<StudyModel[]> = of(null).pipe(
    switchMap(() => this.data.appointment$),
    combineLatestWith(this.data.products$),
    tap(([appointment, products]) => {
      this.currentAppointment = appointment || null
      this.currentProductIds = new Set(products.map((product) => product.id))

      // Determine if the "Add CPT" action should be available.
      this.canAddCPT = (appointment?.studies ?? []).some(
        (study) => study.status !== StudyStatus.CANCELLED
          && (study.products ?? []).some((id) => products.some((product) => product.id === id)),
      )
      this.initializeTableConfig()
    }),
    map(([appointment, products]) => {
      const appointmentStudies = appointment && appointment.studies
        ? appointment.studies
        : []
      const studies = appointmentStudies.flatMap(study => {
        return StudyModel.build(study, products)
      })

      // look for any products on the appointment not on a study
      const studyProducts = new Set(appointmentStudies.flatMap(study => study.products))

      const synthetic = new Set<string>()

      products.forEach(product => {
        if (!studyProducts.has(product.id)) {
          const fakeStudy = new StudyModel({
            products: [product.id],
            productCategories: product.categories,
          }, product)
          synthetic.add(product.id)
          studies.push(fakeStudy)
        }
      })

      this.syntheticProductIds = synthetic

      return studies
    }),
    tap((studies) => this.hasStudies = studies.length > 0),
    // Last before the replay, so destroying the card drops the appointment and product streams
    // rather than leaving them feeding a dead instance.
    takeUntil(this.onDestroy$),
    shareReplay(1),
  )

  public get canEditProcedureConfig(): boolean {
    return this.editable && this.permissionsSvc.isMMXEmployee()
  }

  private initializeTableConfig(): void {
    if (this.tableConfigInitialized) {
      return
    }

    const isFeatureEnabled = this.clinicService.clinic?.settings?.[this.enableEditCptDialogSettingKey] === true
    this.canEditCPT = this.editable && isFeatureEnabled && this.permissionsSvc.isSystemAdministrator()
    this.canDeleteCPT = this.canEditCPT && this.permissionsSvc.instant(Permission.APPOINTMENT_EDIT)
    this.displayedColumns = this.canEditCPT
      ? [...this.baseDisplayedColumns, 'actions']
      : this.baseDisplayedColumns
    this.tableConfigInitialized = true
  }

  constructor(
    public data: AppointmentDataService,
    private clinicService: ClinicService,
    public permissionsSvc: PermissionsService,
    private dialog: MatDialog,
    private api: ApiService,
    private confirmService: ConfirmService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
  ) {}

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public openEditProduct(study: StudyModel): void {
    if (!this.canEditProcedureConfig) {
      return
    }

    const product = study.product
    this.dialog
      .open(ProductDialogComponent, {
        panelClass: 'dialog-form',
        width: '900px',
        data: {
          product,
          fromProceduresCard: true,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((updatedProduct?: ProductModel) => {
        if (updatedProduct) {
          // pull appointments again to get updated information
          this.data.appointmentUpdated$.next()
        }
      })
  }

  // Determines if the current row's CPT can be edited based on whether it is synthetic.
  public canEditRowCPT(study: StudyModel): boolean {
    const productId = study?.product?.id

    return productId != null && !this.syntheticProductIds.has(productId)
  }

  /** Deleting this row leaves the appointment without procedures, which cancels it. */
  public isLastProcedure(study: StudyModel): boolean {
    const productId = study?.product?.id

    return productId != null && !this.hasOtherProcedure(productId)
  }

  /** Mirrors the delete route: counts what the appointment lists, not what the studies hold. */
  private hasOtherProcedure(productId: string): boolean {
    return [...this.currentProductIds].some((id) => id !== productId)
  }

  public deleteCptRow(study: StudyModel): void {
    const appointment = this.currentAppointment
    const productId = study?.product?.id

    if (
      this.deletingProductId != null
      || !this.canDeleteCPT
      || appointment == null
      || productId == null
      || !this.canEditRowCPT(study)
    ) {
      return
    }

    // Claimed before the dialog opens, or a double click would send two deletes.
    this.deletingProductId = productId
    const cancelAppointment = this.isLastProcedure(study)

    this.confirmService.confirm({
      message: cancelAppointment
        ? 'procedures-card.table.actions.confirm-delete-last'
        : 'procedures-card.table.actions.confirm-delete',
      icon: 'delete',
      yesBtnLabel: 'common.delete',
      noBtnLabel: 'common.cancel',
    }).pipe(
      filter(Boolean),
      switchMap(() => this.api.deleteAppointmentProductCpt(appointment.id, productId, { cancelAppointment })),
      takeUntil(this.onDestroy$),
      finalize(() => this.deletingProductId = null),
    ).subscribe({
      next: ({ appointmentCancelled }) => {
        const messageKey = appointmentCancelled
          ? 'procedures-card.delete-cpt-success-appointment-cancelled'
          : 'procedures-card.delete-cpt-success'
        this.snackBar.open(
          this.translateService.instant(messageKey, { name: study.productName }),
          this.translateService.instant('common.dismiss'),
          { duration: appointmentCancelled ? 6000 : 3000 },
        )
        this.data.appointmentUpdated$.next()
      },
      error: (error: HttpErrorResponse) => {
        const validationErrors = error?.error?.errors ?? []
        const message = validationErrors[0]?.message
          || error?.error?.message
          || this.translateService.instant('procedures-card.delete-cpt-failed')
        this.snackBar.open(message, this.translateService.instant('common.dismiss'), { duration: 3000 })
      },
    })
  }

  /** Reconciles one row's codes; every code stays on that row, comma separated. */
  public openEditCPTDialog(study: StudyModel): void {
    if (!this.canEditCPT || !this.currentAppointment || !this.canEditRowCPT(study)) {
      return
    }

    this.openCptDialog({
      mode: 'edit',
      product: study.product,
      appointment: this.currentAppointment,
    })
  }

  /**
   * Adds codes as brand new rows, one line per code.
   */
  public openAddCPTDialog(): void {
    if (
      !this.canEditCPT
      || !this.currentAppointment
      || !this.canAddCPT
    ) {
      return
    }

    this.openCptDialog({
      mode: 'add',
      appointment: this.currentAppointment,
    })
  }

  private openCptDialog(data: CptDialogData): void {
    this.dialog
      .open(CptDialogComponent, {
        panelClass: ['dialog-form', 'cpt-dialog'],
        width: '600px',
        data,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result?: { oldProductId: string } | AddProductCptResult) => {
        if (result) {
          // pull appointments again to render the updated procedure rows
          this.data.appointmentUpdated$.next()
        }
      })
  }
}
